import { ShieldCheck, Lock, FileText, CheckCircle2, UserCheck, RefreshCw, Mail, MapPin, AlertCircle } from "lucide-react";
import { Page, PageHero } from "../components/Layout";
import { FadeIn } from "../components/motion";

const RIGHTS = [
  {
    title: "Right to Access (Section 11)",
    desc: "Data Principals have the right to request a summary of personal data being processed, identity of all entities with whom data has been shared, and any other relevant processing details.",
  },
  {
    title: "Right to Correction & Erasure (Section 12)",
    desc: "You can request the correction of inaccurate data, completion of incomplete records, updating of candidate profile info, and erasure of personal data that is no longer necessary for recruitment or legal obligations.",
  },
  {
    title: "Right of Grievance Redressal (Section 13)",
    desc: "A readily accessible grievance redressal mechanism is provided. Our Data Protection & Grievance Officer responds to all privacy inquiries within 48 business hours.",
  },
  {
    title: "Right to Nominate (Section 14)",
    desc: "You have the right to nominate any other individual to exercise your data principal rights in the event of death or incapacity.",
  },
  {
    title: "Right to Withdraw Consent (Section 6)",
    desc: "Consent can be withdrawn at any time with ease, without affecting the lawfulness of any processing carried out prior to withdrawal.",
  },
];

const DATA_CATEGORIES = [
  {
    cat: "Candidate Profile & Identification",
    items: "Full name, contact telephone, email, photograph, date of birth, passport copy, and government identification records required for overseas emigration clearance.",
  },
  {
    cat: "Professional & Qualification Records",
    items: "Resume, educational transcripts, technical trade test certifications, skill ratings, past overseas employment tenure, and client interview assessments.",
  },
  {
    cat: "Mobilization & Statutory Compliance Data",
    items: "Medical fitness certificates required by GCC Health Council / destination country labor ministries, police clearance certificates (PCC), visa stamping status, and flight mobilization logs.",
  },
  {
    cat: "Enterprise Client & Commercial Data",
    items: "Authorized corporate representative name, business email, corporate phone, hiring demands, and contractual service agreements.",
  },
];

export default function PrivacyPolicy() {
  return (
    <Page testId="privacy-policy-page">
      <PageHero
        image="/assets/bg-about.jpg"
        eyebrow="Compliance & Governance"
        title={<>Digital Personal Data Protection <span className="text-[#D4AF37]">(DPDP) Notice</span></>}
        sub="Notice under Section 5 of the Digital Personal Data Protection Act, 2023 (India) and international privacy standards."
      />

      <section className="bg-[#FAF7F2] py-16 sm:py-24" data-testid="dpdp-content-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 space-y-12">
          
          {/* Summary Callout Card */}
          <FadeIn className="bg-white p-8 sm:p-10 rounded-3xl border-t-4 border-[#811F5D] border-x border-b border-[#811F5D]/10 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#811F5D]/10 flex items-center justify-center shrink-0 text-[#811F5D]">
                <ShieldCheck size={26} />
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-[#811F5D] font-bold">Data Fiduciary Commitment</span>
                <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl mt-1 mb-3">Commitment to Data Privacy & Transparency</h2>
                <p className="text-[#6B5A64] text-sm sm:text-base leading-relaxed">
                  Vardhamaan Consultancy Services (“Vardhamaan”, “we”, “us”, or “our”) is committed to protecting the privacy, confidentiality, and integrity of all personal data entrusted to us. As an ISO 9001:2015 certified, Ministry of External Affairs (MEA) registered global recruitment consultancy, we operate in strict alignment with the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> of India and applicable global workforce compliance standards.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-xs font-mono">
                  <span className="px-3 py-1 bg-[#FAF7F2] text-[#4A1136] rounded-full border border-[#811F5D]/15 font-semibold">Data Fiduciary: Vardhamaan Consultancy Services</span>
                  <span className="px-3 py-1 bg-[#FAF7F2] text-[#4A1136] rounded-full border border-[#811F5D]/15 font-semibold">Jurisdiction: Republic of India</span>
                  <span className="px-3 py-1 bg-[#FAF7F2] text-[#4A1136] rounded-full border border-[#811F5D]/15 font-semibold">MEA Registration Compliance</span>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Section 1: Categories of Data Collected */}
          <FadeIn delay={0.08} className="bg-white p-8 sm:p-10 rounded-3xl border border-[#811F5D]/10 shadow-md">
            <h3 className="font-heading font-extrabold text-[#2A0E20] text-xl sm:text-2xl mb-4 flex items-center gap-3">
              <FileText size={22} className="text-[#D4AF37]" />
              1. Categories of Personal Data Processed (Section 5 Notice)
            </h3>
            <p className="text-[#6B5A64] text-sm leading-relaxed mb-6">
              In accordance with Section 5 of the DPDP Act, 2023, personal data is collected solely for specified recruitment, trade testing, visa processing, and statutory mobilization purposes:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {DATA_CATEGORIES.map((cat, idx) => (
                <div key={idx} className="p-5 rounded-2xl bg-[#FAF7F2] border border-[#811F5D]/10">
                  <h4 className="font-heading font-bold text-[#4A1136] text-sm mb-2">{cat.cat}</h4>
                  <p className="text-xs text-[#6B5A64] leading-relaxed">{cat.items}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Section 2: Lawful Grounds & Specified Purpose */}
          <FadeIn delay={0.12} className="bg-white p-8 sm:p-10 rounded-3xl border border-[#811F5D]/10 shadow-md">
            <h3 className="font-heading font-extrabold text-[#2A0E20] text-xl sm:text-2xl mb-4 flex items-center gap-3">
              <Lock size={22} className="text-[#811F5D]" />
              2. Lawful Grounds and Specific Purposes of Processing
            </h3>
            <div className="space-y-3 text-sm text-[#6B5A64] leading-relaxed">
              <p>
                We process your personal data only on the basis of <strong>freely given, specific, informed, and unambiguous consent</strong> (Section 6) or for <strong>legitimate uses</strong> recognized under Section 7 of the Act. Specific purposes include:
              </p>
              <ul className="space-y-2.5 pt-2">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span><strong>Candidate Evaluation:</strong> Matching candidate profiles against client requirements in GCC, Eastern Europe, and Caribbean sectors.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span><strong>Trade Testing & Certification:</strong> Facilitating authorized skill assessments, trade tests, and technical certifications.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span><strong>Emigration & Visa Clearance:</strong> Processing MEA document attestation, visa stamping, and government emigration clearances.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span><strong>Mobilization Support:</strong> In-house travel ticketing and deployment coordination through our entity Vardhamaan Yanharif.</span>
                </li>
              </ul>
            </div>
          </FadeIn>

          {/* Section 3: Data Principal Rights */}
          <FadeIn delay={0.16} className="bg-white p-8 sm:p-10 rounded-3xl border-t-4 border-[#D4AF37] border-x border-b border-[#811F5D]/10 shadow-md">
            <h3 className="font-heading font-extrabold text-[#2A0E20] text-xl sm:text-2xl mb-4 flex items-center gap-3">
              <UserCheck size={22} className="text-[#D4AF37]" />
              3. Data Principal Rights (Sections 11–14 of DPDP Act)
            </h3>
            <p className="text-[#6B5A64] text-sm leading-relaxed mb-6">
              As a Data Principal under Indian law, you are entitled to the following enforceable statutory rights:
            </p>
            <div className="space-y-4">
              {RIGHTS.map((r, i) => (
                <div key={i} className="p-4 rounded-xl bg-[#FAF7F2] border border-[#811F5D]/10">
                  <h4 className="font-heading font-bold text-[#4A1136] text-sm mb-1">{r.title}</h4>
                  <p className="text-xs text-[#6B5A64] leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Section 4: Cross-Border Transfers & Retention */}
          <FadeIn delay={0.2} className="bg-white p-8 sm:p-10 rounded-3xl border border-[#811F5D]/10 shadow-md">
            <h3 className="font-heading font-extrabold text-[#2A0E20] text-xl sm:text-2xl mb-4 flex items-center gap-3">
              <RefreshCw size={22} className="text-[#811F5D]" />
              4. Cross-Border Transfers & Retention Schedule
            </h3>
            <div className="space-y-4 text-sm text-[#6B5A64] leading-relaxed">
              <div>
                <h4 className="font-bold text-[#2A0E20] mb-1">Cross-Border Data Transfers (Section 16)</h4>
                <p>
                  Because we mobilize manpower globally, candidate personal data is transferred across international borders to licensed foreign employers, sponsor entities, and immigration authorities in destinations including Qatar, Kuwait, UAE, Saudi Arabia, Oman, Bahrain, Eastern Europe, and the Caribbean. All transfers are governed by secure data processing arrangements and client agreements requiring equivalent security safeguards.
                </p>
              </div>
              <div className="pt-2">
                <h4 className="font-bold text-[#2A0E20] mb-1">Purpose-Based Data Retention (Section 8)</h4>
                <p>
                  Personal data is retained only for the duration necessary to satisfy the purpose for which it was collected or to comply with statutory record-keeping periods mandated by the Ministry of External Affairs (MEA) and Indian Emigration regulations. Once statutory periods expire, data is permanently erased or irreversibly anonymized.
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Section 5: Grievance Officer & Contact Details */}
          <FadeIn delay={0.24} className="bg-[#4A1136] text-white p-8 sm:p-10 rounded-3xl shadow-xl relative overflow-hidden border border-[#D4AF37]/30">
            <div className="relative z-10">
              <span className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] font-semibold flex items-center gap-2 mb-2">
                <AlertCircle size={14} /> Designated Grievance Officer
              </span>
              <h3 className="font-heading font-extrabold text-2xl sm:text-3xl mb-4">Grievance Redressal & DPO Contact</h3>
              <p className="text-white/80 text-sm leading-relaxed mb-6 max-w-2xl">
                To exercise your rights under the DPDP Act 2023, withdraw consent, or file a privacy grievance, please contact our designated Data Protection Officer:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-white/10 p-5 rounded-2xl border border-white/10 backdrop-blur-sm">
                  <p className="font-bold text-[#D4AF37] text-base mb-1">Data Protection & Grievance Officer</p>
                  <p className="text-white/80 text-xs mb-3">Vardhamaan Consultancy Services</p>
                  <div className="space-y-2 text-xs text-white/90">
                    <p className="flex items-center gap-2"><Mail size={14} className="text-[#D4AF37]" /> privacy@vardhamaanconsultancyservices.com</p>
                    <p className="flex items-center gap-2"><Mail size={14} className="text-[#D4AF37]" /> amol@vardhamaanconsultancyservices.com</p>
                  </div>
                </div>

                <div className="bg-white/10 p-5 rounded-2xl border border-white/10 backdrop-blur-sm">
                  <p className="font-bold text-[#D4AF37] text-base mb-1">Head Office Address</p>
                  <p className="flex items-start gap-2 text-xs text-white/80 leading-relaxed mt-2">
                    <MapPin size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    702, Dattani Prism-1, Commercial Premises Cooperative Society Limited, Vasai West, Mumbai, Maharashtra — 401207, India
                  </p>
                  <p className="mt-3 text-[11px] font-mono text-[#D4AF37]">Acknowledgment SLA: within 48 hours</p>
                </div>
              </div>
            </div>
          </FadeIn>

        </div>
      </section>
    </Page>
  );
}
